import { useEffect, useRef, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Save,
  Loader2,
  Palette,
  Globe,
  Phone,
  DollarSign,
  Percent,
  Gift,
  Settings2,
  Upload,
  MessageCircle,
  Send,
  Instagram,
  Link2,
  AlertTriangle,
  Download,
  Trash2,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { TopBanner } from '@/components/TopBanner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import { useAuthStore } from '@/stores/auth.store';

export const Route = createFileRoute('/_authenticated/ajustes')({
  component: AjustesPage,
});

const widgetButtonSchema = z.object({
  mode: z.enum(['option', 'link']),
  url: z.string().optional(),
});

const settingsSchema = z.object({
  siteName: z.string().min(1, 'Requerido'),
  siteUrl: z.string().url('URL inválida'),
  timezone: z.string(),
  whatsappSupport: z.string().optional(),
  minWithdrawal: z.coerce.number().min(0),
  minDeposit: z.coerce.number().min(0),
  welcomeBonusPercent: z.coerce.number().min(0).max(500),
  welcomeBonusMinDeposit: z.coerce.number().min(0),
  referralPrize: z.coerce.number().min(0),
  operatingMode: z.enum(['manual', 'internal', 'lambda']),
  autoWithdrawalThreshold: z.coerce.number().min(0),
  maxWithdrawalsPerDay: z.coerce.number().min(0),
  themeColor: z.string().regex(/^#[0-9A-Fa-f]{6}$/, 'Color hex inválido'),
  // --- Parte I: paridad Charly ---
  logoUrl: z.string().optional(),
  faviconUrl: z.string().optional(),
  community: z
    .object({
      whatsapp: z.string().optional(),
      telegram: z.string().optional(),
      instagram: z.string().optional(),
    })
    .optional(),
  widgetButtons: z
    .object({
      cargar: widgetButtonSchema.optional(),
      retirar: widgetButtonSchema.optional(),
      soporte: widgetButtonSchema.optional(),
      referir: widgetButtonSchema.optional(),
    })
    .optional(),
});

type SettingsForm = z.infer<typeof settingsSchema>;

interface SettingsData {
  siteName: string;
  siteUrl: string;
  timezone: string;
  whatsappSupport?: string;
  minWithdrawal: number;
  minDeposit: number;
  welcomeBonusPercent: number;
  welcomeBonusMinDeposit: number;
  referralPrize: number;
  operatingMode: 'manual' | 'internal' | 'lambda';
  autoWithdrawalThreshold: number;
  maxWithdrawalsPerDay: number;
  themeColor: string;
  logoUrl?: string;
  faviconUrl?: string;
  community?: {
    whatsapp?: string;
    telegram?: string;
    instagram?: string;
  };
  widgetButtons?: {
    cargar?: { mode: 'option' | 'link'; url?: string };
    retirar?: { mode: 'option' | 'link'; url?: string };
    soporte?: { mode: 'option' | 'link'; url?: string };
    referir?: { mode: 'option' | 'link'; url?: string };
  };
}

const WIDGET_BUTTON_LABELS: Record<'cargar' | 'retirar' | 'soporte' | 'referir', string> = {
  cargar: 'Cargar fichas',
  retirar: 'Retirar',
  soporte: 'Soporte',
  referir: 'Referir un amigo',
};

const BORRAR_CONFIRM_WORD = 'BORRAR';

function AjustesPage() {
  const qc = useQueryClient();
  const role = useAuthStore((s) => s.user?.role);
  const tenantSlug = useAuthStore((s) => s.tenantSlug);
  const isMaster = role === 'master';

  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingFavicon, setUploadingFavicon] = useState(false);
  const [clearChatsOpen, setClearChatsOpen] = useState(false);
  const [confirmWord, setConfirmWord] = useState('');
  const logoInputRef = useRef<HTMLInputElement>(null);
  const faviconInputRef = useRef<HTMLInputElement>(null);

  const { data: settings, isLoading } = useQuery({
    queryKey: ['settings'],
    queryFn: async () => {
      const { data } = await apiClient.get('/admin/settings');
      return data as SettingsData;
    },
  });

  const mutation = useMutation({
    mutationFn: async (body: SettingsForm) => {
      const { data } = await apiClient.put('/admin/settings', body);
      return data;
    },
    onSuccess: () => {
      toast.success('Ajustes guardados');
      qc.invalidateQueries({ queryKey: ['settings'] });
    },
    onError: () => toast.error('Error al guardar'),
  });

  const exportMutation = useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.get('/admin/settings/export');
      return data;
    },
    onSuccess: (data) => {
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `export-${tenantSlug || 'tenant'}-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Datos exportados');
    },
    onError: () => toast.error('Error al exportar'),
  });

  // 🚨 DESTRUCTIVO: borra TODAS las conversaciones + mensajes del tenant.
  // Solo se dispara desde el modal con confirmación fuerte (escribir BORRAR).
  const clearChatsMutation = useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.post('/admin/settings/clear-chats');
      return data;
    },
    onSuccess: () => {
      toast.success('Chats eliminados');
      setClearChatsOpen(false);
      setConfirmWord('');
      qc.invalidateQueries({ queryKey: ['conversations'] });
    },
    onError: () => toast.error('Error al limpiar los chats'),
  });

  const form = useForm<SettingsForm>({
    resolver: zodResolver(settingsSchema),
    defaultValues: {
      siteName: 'Clone Chat',
      siteUrl: 'https://localhost:5173',
      timezone: 'America/Argentina/Buenos_Aires',
      whatsappSupport: '',
      minWithdrawal: 1000,
      minDeposit: 500,
      welcomeBonusPercent: 200,
      welcomeBonusMinDeposit: 1000,
      referralPrize: 0,
      operatingMode: 'lambda',
      autoWithdrawalThreshold: 50000,
      maxWithdrawalsPerDay: 0,
      themeColor: '#d4a843',
      logoUrl: '',
      faviconUrl: '',
      community: { whatsapp: '', telegram: '', instagram: '' },
      widgetButtons: {
        cargar: { mode: 'option', url: '' },
        retirar: { mode: 'option', url: '' },
        soporte: { mode: 'option', url: '' },
        referir: { mode: 'option', url: '' },
      },
    },
  });

  useEffect(() => {
    if (settings) {
      form.reset(settings);
    }
  }, [settings, form]);

  const onSubmit = (values: SettingsForm) => {
    mutation.mutate(values);
  };

  const uploadFile = async (file: File): Promise<string> => {
    const formData = new FormData();
    formData.append('file', file);
    const base = (import.meta.env.VITE_API_URL as string | undefined) || '/api';
    const res = await fetch(`${base}/chat/widget/upload?tenantSlug=${encodeURIComponent(tenantSlug || '')}`, {
      method: 'POST',
      body: formData,
    });
    const body = (await res.json()) as { ok: boolean; url?: string; error?: string };
    if (!res.ok || !body.ok || !body.url) {
      throw new Error(body.error || 'upload_failed');
    }
    return body.url;
  };

  const handleLogoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingLogo(true);
    try {
      const url = await uploadFile(file);
      form.setValue('logoUrl', url, { shouldDirty: true });
      toast.success('Logo subido. Recordá guardar los ajustes.');
    } catch {
      toast.error('Error al subir el logo');
    } finally {
      setUploadingLogo(false);
      if (logoInputRef.current) logoInputRef.current.value = '';
    }
  };

  const handleFaviconChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingFavicon(true);
    try {
      const url = await uploadFile(file);
      form.setValue('faviconUrl', url, { shouldDirty: true });
      toast.success('Favicon subido. Recordá guardar los ajustes.');
    } catch {
      toast.error('Error al subir el favicon');
    } finally {
      setUploadingFavicon(false);
      if (faviconInputRef.current) faviconInputRef.current.value = '';
    }
  };

  if (isLoading) {
    return (
      <>
        <TopBanner title="Ajustes" subtitle="Configuración del tenant" />
        <div className="flex-1 overflow-auto p-6 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </>
    );
  }

  const watchedLogoUrl = form.watch('logoUrl');
  const watchedFaviconUrl = form.watch('faviconUrl');
  const widgetButtonKeys: Array<'cargar' | 'retirar' | 'soporte' | 'referir'> = [
    'cargar',
    'retirar',
    'soporte',
    'referir',
  ];

  return (
    <>
      <TopBanner title="Ajustes" subtitle="Configuración del tenant" />
      <div className="flex-1 overflow-auto p-6">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6 max-w-4xl">
            {/* Personalización */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Palette className="h-5 w-5" />
                  Personalización
                </CardTitle>
                <CardDescription>Tema, colores, logo y favicon del sitio</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="siteName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Nombre del sitio</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="themeColor"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Color principal</FormLabel>
                        <div className="flex gap-2">
                          <FormControl>
                            <Input {...field} />
                          </FormControl>
                          <input
                            type="color"
                            value={field.value}
                            onChange={(e) => field.onChange(e.target.value)}
                            className="h-10 w-10 rounded border p-1"
                          />
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Logo</label>
                    <div className="flex items-center gap-3">
                      {watchedLogoUrl ? (
                        <img
                          src={watchedLogoUrl}
                          alt="Logo actual"
                          className="h-10 w-10 rounded border object-contain bg-white"
                        />
                      ) : null}
                      <input
                        ref={logoInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleLogoChange}
                        className="hidden"
                        id="logo-upload"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={uploadingLogo}
                        onClick={() => logoInputRef.current?.click()}
                      >
                        {uploadingLogo ? (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                          <Upload className="mr-2 h-4 w-4" />
                        )}
                        Subir logo
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Favicon</label>
                    <div className="flex items-center gap-3">
                      {watchedFaviconUrl ? (
                        <img
                          src={watchedFaviconUrl}
                          alt="Favicon actual"
                          className="h-10 w-10 rounded border object-contain bg-white"
                        />
                      ) : null}
                      <input
                        ref={faviconInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleFaviconChange}
                        className="hidden"
                        id="favicon-upload"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={uploadingFavicon}
                        onClick={() => faviconInputRef.current?.click()}
                      >
                        {uploadingFavicon ? (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                          <Upload className="mr-2 h-4 w-4" />
                        )}
                        Subir favicon
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Config General */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Globe className="h-5 w-5" />
                  Configuración General
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="siteUrl"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>URL del sitio</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="timezone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Zona horaria</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="America/Argentina/Buenos_Aires">Buenos Aires (GMT-3)</SelectItem>
                            <SelectItem value="America/Mexico_City">Ciudad de México (GMT-6)</SelectItem>
                            <SelectItem value="America/Bogota">Bogotá (GMT-5)</SelectItem>
                            <SelectItem value="America/Santiago">Santiago (GMT-4)</SelectItem>
                            <SelectItem value="Europe/Madrid">Madrid (GMT+1)</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="whatsappSupport"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>WhatsApp Soporte</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="+5491112345678" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Comunidad */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageCircle className="h-5 w-5" />
                  Comunidad
                </CardTitle>
                <CardDescription>
                  Links que aparecen en el popup de "fichas cargadas" del widget
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 md:grid-cols-3">
                  <FormField
                    control={form.control}
                    name="community.whatsapp"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="flex items-center gap-1">
                          <Phone className="h-3.5 w-3.5" />
                          WhatsApp
                        </FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="https://wa.me/5491112345678" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="community.telegram"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="flex items-center gap-1">
                          <Send className="h-3.5 w-3.5" />
                          Telegram
                        </FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="https://t.me/tucanal" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="community.instagram"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="flex items-center gap-1">
                          <Instagram className="h-3.5 w-3.5" />
                          Instagram
                        </FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="https://instagram.com/tucuenta" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Botones del widget */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Link2 className="h-5 w-5" />
                  Botones del widget
                </CardTitle>
                <CardDescription>
                  Cada botón puede abrir un link externo o seguir el flujo normal del chat
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {widgetButtonKeys.map((key) => {
                  const mode = form.watch(`widgetButtons.${key}.mode`);
                  return (
                    <div key={key} className="grid gap-4 md:grid-cols-2 items-start border-b pb-4 last:border-b-0 last:pb-0">
                      <FormField
                        control={form.control}
                        name={`widgetButtons.${key}.mode`}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{WIDGET_BUTTON_LABELS[key]}</FormLabel>
                            <Select onValueChange={field.onChange} defaultValue={field.value}>
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="option">Opción del flujo</SelectItem>
                                <SelectItem value="link">Link externo</SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      {mode === 'link' ? (
                        <FormField
                          control={form.control}
                          name={`widgetButtons.${key}.url`}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>URL del link</FormLabel>
                              <FormControl>
                                <Input {...field} placeholder="https://..." />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      ) : null}
                    </div>
                  );
                })}
              </CardContent>
            </Card>

            {/* Límites y Bonos */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5" />
                  Límites y Bonos
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 md:grid-cols-3">
                  <FormField
                    control={form.control}
                    name="minWithdrawal"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Mínimo retiro ($)</FormLabel>
                        <FormControl>
                          <Input type="number" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="minDeposit"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Mínimo depósito ($)</FormLabel>
                        <FormControl>
                          <Input type="number" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="welcomeBonusPercent"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="flex items-center gap-1">
                          <Gift className="h-3.5 w-3.5" />
                          Bono bienvenida (%)
                        </FormLabel>
                        <FormControl>
                          <Input type="number" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="welcomeBonusMinDeposit"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Depósito mínimo para bono ($)</FormLabel>
                        <FormControl>
                          <Input type="number" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="referralPrize"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Premio por referido ($)</FormLabel>
                        <FormControl>
                          <Input type="number" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Modo Operativo */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings2 className="h-5 w-5" />
                  Modo Operativo
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="operatingMode"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Modo de retiro</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="manual">Manual (siempre requiere aprobación)</SelectItem>
                            <SelectItem value="internal">Interno (procesa internamente)</SelectItem>
                            <SelectItem value="lambda">Lambda (automático con wallet)</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormDescription>
                          Lambda = retiro automático vía wallet (requiere history)
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="autoWithdrawalThreshold"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Umbral manual ($)</FormLabel>
                        <FormControl>
                          <Input type="number" {...field} />
                        </FormControl>
                        <FormDescription>
                          Retiros ≥ este monto requieren aprobación manual
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="maxWithdrawalsPerDay"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Retiros por día (0 = ilimitado)</FormLabel>
                        <FormControl>
                          <Input type="number" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </CardContent>
            </Card>

            <div className="flex justify-end">
              <Button type="submit" disabled={mutation.isPending} size="lg">
                {mutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                <Save className="mr-2 h-4 w-4" />
                Guardar ajustes
              </Button>
            </div>

            {/* Zona Peligrosa */}
            <Card className="border-destructive/50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-destructive">
                  <AlertTriangle className="h-5 w-5" />
                  Zona Peligrosa
                </CardTitle>
                <CardDescription>
                  Exportar datos del tenant o eliminar permanentemente todas las conversaciones
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between rounded-md border p-4">
                  <div>
                    <p className="font-medium">Exportar datos</p>
                    <p className="text-sm text-muted-foreground">
                      Descarga un JSON con clientes y pagos del tenant (solo lectura)
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    disabled={exportMutation.isPending}
                    onClick={() => exportMutation.mutate()}
                  >
                    {exportMutation.isPending ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Download className="mr-2 h-4 w-4" />
                    )}
                    Exportar datos
                  </Button>
                </div>

                <div className="flex items-center justify-between rounded-md border border-destructive/50 p-4">
                  <div>
                    <p className="font-medium text-destructive">Limpiar chats</p>
                    <p className="text-sm text-muted-foreground">
                      Elimina PERMANENTEMENTE todas las conversaciones y mensajes de este tenant.
                      No se puede deshacer.
                      {!isMaster ? ' Requiere rol master.' : ''}
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="destructive"
                    disabled={!isMaster}
                    onClick={() => setClearChatsOpen(true)}
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Limpiar chats
                  </Button>
                </div>
              </CardContent>
            </Card>
          </form>
        </Form>
      </div>

      <Dialog
        open={clearChatsOpen}
        onOpenChange={(open) => {
          setClearChatsOpen(open);
          if (!open) setConfirmWord('');
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              Limpiar chats — acción irreversible
            </DialogTitle>
            <DialogDescription>
              Esto va a borrar TODAS las conversaciones y mensajes de este tenant de forma
              permanente. No hay vuelta atrás. Para confirmar, escribí{' '}
              <strong>{BORRAR_CONFIRM_WORD}</strong> abajo.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Input
              value={confirmWord}
              onChange={(e) => setConfirmWord(e.target.value)}
              placeholder={BORRAR_CONFIRM_WORD}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setClearChatsOpen(false);
                setConfirmWord('');
              }}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={confirmWord !== BORRAR_CONFIRM_WORD || clearChatsMutation.isPending}
              onClick={() => clearChatsMutation.mutate()}
            >
              {clearChatsMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Sí, borrar todo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
